import { useEffect, useRef } from "react";

export const ANDROID_TOUCH_INPUT_MODE = "touch";
export const ANDROID_WEBGPU_GUIDANCE =
  "Open this viewer in the latest Chrome for Android.";

const DEFAULT_FOCUS = Object.freeze({ focusX: 0.5, focusY: 0.5 });

const getGlobalNavigator = () =>
  typeof navigator === "undefined" ? undefined : navigator;

export const isAndroidPlatform = (navigatorLike = getGlobalNavigator()) => {
  if (!navigatorLike) {
    return false;
  }

  const platform = navigatorLike.userAgentData?.platform;
  if (typeof platform === "string" && platform.trim()) {
    return /Android/i.test(platform);
  }

  return /Android/i.test(navigatorLike.userAgent || "");
};

export const isAndroidTouchDevice = (navigatorLike = getGlobalNavigator()) => {
  if (!navigatorLike) {
    return false;
  }

  return isAndroidPlatform(navigatorLike) && Number(navigatorLike.maxTouchPoints) > 0;
};

export const getAndroidWebGpuFallbackDetail = (
  navigatorLike = getGlobalNavigator(),
) => (isAndroidPlatform(navigatorLike) ? ANDROID_WEBGPU_GUIDANCE : "");

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getTouches = (touchList) => Array.from(touchList || []);

const getTouchId = (touch, index) =>
  typeof touch?.identifier === "number" || typeof touch?.identifier === "string"
    ? touch.identifier
    : index;

const getTouchEntries = (touchList) =>
  getTouches(touchList).map((touch, index) => ({
    id: getTouchId(touch, index),
    touch,
  }));

const getTouchPairDistance = (firstTouch, secondTouch) => {
  const deltaX = Number(secondTouch?.clientX) - Number(firstTouch?.clientX);
  const deltaY = Number(secondTouch?.clientY) - Number(firstTouch?.clientY);
  const distance = Math.hypot(deltaX, deltaY);
  return Number.isFinite(distance) && distance > 0 ? distance : null;
};

const getNormalizedFocus = (firstTouch, secondTouch, bounds) => {
  const left = Number(bounds?.left) || 0;
  const top = Number(bounds?.top) || 0;
  const width = Number(bounds?.width);
  const height = Number(bounds?.height);
  const midpointX =
    (Number(firstTouch?.clientX) + Number(secondTouch?.clientX)) / 2;
  const midpointY =
    (Number(firstTouch?.clientY) + Number(secondTouch?.clientY)) / 2;

  if (
    !Number.isFinite(midpointX) ||
    !Number.isFinite(midpointY) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return DEFAULT_FOCUS;
  }

  return {
    focusX: clamp((midpointX - left) / width, 0, 1),
    focusY: clamp((midpointY - top) / height, 0, 1),
  };
};

const getFirstPair = (touches) => {
  const entries = getTouchEntries(touches);
  return entries.length >= 2 ? [entries[0], entries[1]] : null;
};

const getTrackedPair = (touches, firstId, secondId) => {
  const entries = getTouchEntries(touches);
  const first = entries.find((entry) => entry.id === firstId);
  const second = entries.find((entry) => entry.id === secondId);
  return first && second ? [first, second] : null;
};

const consumeTouchEvent = (event) => {
  if (event?.cancelable !== false) {
    event?.preventDefault?.();
  }

  event?.stopPropagation?.();
  event?.stopImmediatePropagation?.();
};

const defineTouchListProperty = (event, property, value) => {
  try {
    Object.defineProperty(event, property, {
      configurable: true,
      value,
    });
  } catch (error) {
    // Native TouchEvent fields are read-only in some browsers. The event is
    // still useful as a cancellation signal without the synthetic list.
  }
};

const createTouchCancelEvent = (sourceEvent) => {
  const changedTouches = getTouches(sourceEvent?.touches);

  if (typeof TouchEvent === "function") {
    try {
      return new TouchEvent("touchcancel", {
        bubbles: true,
        cancelable: true,
        changedTouches,
        targetTouches: [],
        touches: [],
      });
    } catch (error) {
      // Fall back below for test environments and older event constructors.
    }
  }

  const event = new Event("touchcancel", {
    bubbles: true,
    cancelable: true,
  });
  defineTouchListProperty(event, "changedTouches", changedTouches);
  defineTouchListProperty(event, "targetTouches", []);
  defineTouchListProperty(event, "touches", []);
  return event;
};

const createPointerCancelEvent = () => {
  if (typeof PointerEvent === "function") {
    return new PointerEvent("pointercancel", {
      bubbles: true,
      cancelable: true,
      isPrimary: true,
      pointerId: 1,
      pointerType: "touch",
    });
  }

  return new Event("pointercancel", {
    bubbles: true,
    cancelable: true,
  });
};

export const dispatchUnityTouchCancel = (container, sourceEvent) => {
  const target =
    container?.querySelector?.("canvas") ||
    container?.firstElementChild ||
    container;

  if (!target || typeof target.dispatchEvent !== "function") {
    return;
  }

  target.dispatchEvent(createPointerCancelEvent());
  target.dispatchEvent(createTouchCancelEvent(sourceEvent));
};

export const createAndroidPinchGestureBridge = ({
  cancelRawTouchInput,
  getBounds,
  sendGesture,
}) => {
  const state = {
    active: false,
    consumingRemainder: false,
    firstId: null,
    secondId: null,
    previousDistance: null,
    lastFocus: DEFAULT_FOCUS,
  };

  const reset = () => {
    state.active = false;
    state.consumingRemainder = false;
    state.firstId = null;
    state.secondId = null;
    state.previousDistance = null;
    state.lastFocus = DEFAULT_FOCUS;
  };

  const send = (phase, scaleDelta = 1, focus = state.lastFocus) => {
    const safeScaleDelta =
      phase === "changed" &&
      Number.isFinite(scaleDelta) &&
      scaleDelta > 0
        ? scaleDelta
        : 1;
    const safeFocus = focus || state.lastFocus || DEFAULT_FOCUS;
    state.lastFocus = safeFocus;

    sendGesture({
      phase,
      scaleDelta: safeScaleDelta,
      focusX: safeFocus.focusX,
      focusY: safeFocus.focusY,
    });
  };

  const rebase = (touches) => {
    const pair = getFirstPair(touches);
    if (!pair) {
      state.previousDistance = null;
      return false;
    }

    const [first, second] = pair;
    state.firstId = first.id;
    state.secondId = second.id;
    state.previousDistance = getTouchPairDistance(first.touch, second.touch);
    state.lastFocus = getNormalizedFocus(
      first.touch,
      second.touch,
      getBounds?.(),
    );
    return true;
  };

  const begin = (event) => {
    if (!rebase(event?.touches)) {
      return false;
    }

    cancelRawTouchInput?.(event);
    state.active = true;
    state.consumingRemainder = false;
    send("began", 1, state.lastFocus);
    consumeTouchEvent(event);
    return true;
  };

  const handleTouchStart = (event) => {
    if (state.active || state.consumingRemainder) {
      consumeTouchEvent(event);
      return true;
    }

    if (getTouches(event?.touches).length < 2) {
      return false;
    }

    return begin(event);
  };

  const handleTouchMove = (event) => {
    if (!state.active) {
      if (state.consumingRemainder) {
        consumeTouchEvent(event);
        if (getTouches(event?.touches).length === 0) {
          reset();
        }
        return true;
      }

      if (getTouches(event?.touches).length >= 2) {
        return begin(event);
      }

      return false;
    }

    consumeTouchEvent(event);

    const trackedPair = getTrackedPair(
      event?.touches,
      state.firstId,
      state.secondId,
    );

    if (!trackedPair) {
      if (getTouches(event?.touches).length >= 2) {
        rebase(event?.touches);
      }
      return true;
    }

    const [first, second] = trackedPair;
    const distance = getTouchPairDistance(first.touch, second.touch);
    if (!distance) {
      return true;
    }

    if (!state.previousDistance) {
      state.previousDistance = distance;
      state.lastFocus = getNormalizedFocus(
        first.touch,
        second.touch,
        getBounds?.(),
      );
      return true;
    }

    const scaleDelta = distance / state.previousDistance;
    state.previousDistance = distance;

    if (!Number.isFinite(scaleDelta) || scaleDelta <= 0) {
      return true;
    }

    const focus = getNormalizedFocus(first.touch, second.touch, getBounds?.());
    send("changed", scaleDelta, focus);
    return true;
  };

  const handleTouchEnd = (event) => {
    if (!state.active) {
      if (state.consumingRemainder) {
        consumeTouchEvent(event);
        if (getTouches(event?.touches).length === 0) {
          reset();
        }
        return true;
      }

      return false;
    }

    consumeTouchEvent(event);

    const remainingTouches = getTouches(event?.touches);
    if (remainingTouches.length >= 2) {
      rebase(remainingTouches);
      return true;
    }

    send("ended", 1, state.lastFocus);
    state.active = false;
    state.consumingRemainder = remainingTouches.length > 0;

    if (!state.consumingRemainder) {
      reset();
    }

    return true;
  };

  const handleTouchCancel = (event) => {
    if (!state.active && !state.consumingRemainder) {
      return false;
    }

    consumeTouchEvent(event);
    if (state.active) {
      send("cancelled", 1, state.lastFocus);
    }
    reset();
    return true;
  };

  const cancel = () => {
    if (state.active) {
      send("cancelled", 1, state.lastFocus);
    }
    reset();
  };

  return {
    cancel,
    handleTouchCancel,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    reset,
  };
};

const TOUCH_LISTENER_OPTIONS = {
  capture: true,
  passive: false,
};

export const useAndroidPinchGestureBridge = ({
  cancelRawTouchInput,
  containerRef,
  enabled,
  sendGesture,
}) => {
  const cancelRawTouchInputRef = useRef(cancelRawTouchInput);
  const sendGestureRef = useRef(sendGesture);
  const bridgeRef = useRef(null);

  useEffect(() => {
    cancelRawTouchInputRef.current = cancelRawTouchInput;
  }, [cancelRawTouchInput]);

  useEffect(() => {
    sendGestureRef.current = sendGesture;
  }, [sendGesture]);

  if (!bridgeRef.current) {
    bridgeRef.current = createAndroidPinchGestureBridge({
      cancelRawTouchInput: (event) => cancelRawTouchInputRef.current?.(event),
      getBounds: () => containerRef.current?.getBoundingClientRect?.(),
      sendGesture: (payload) => sendGestureRef.current?.(payload),
    });
  }

  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!enabled) {
      bridge.cancel();
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      bridge.cancel();
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        bridge.cancel();
      }
    };

    container.addEventListener(
      "touchstart",
      bridge.handleTouchStart,
      TOUCH_LISTENER_OPTIONS,
    );
    container.addEventListener(
      "touchmove",
      bridge.handleTouchMove,
      TOUCH_LISTENER_OPTIONS,
    );
    container.addEventListener(
      "touchend",
      bridge.handleTouchEnd,
      TOUCH_LISTENER_OPTIONS,
    );
    container.addEventListener(
      "touchcancel",
      bridge.handleTouchCancel,
      TOUCH_LISTENER_OPTIONS,
    );
    window.addEventListener("blur", bridge.cancel);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      container.removeEventListener(
        "touchstart",
        bridge.handleTouchStart,
        TOUCH_LISTENER_OPTIONS,
      );
      container.removeEventListener(
        "touchmove",
        bridge.handleTouchMove,
        TOUCH_LISTENER_OPTIONS,
      );
      container.removeEventListener(
        "touchend",
        bridge.handleTouchEnd,
        TOUCH_LISTENER_OPTIONS,
      );
      container.removeEventListener(
        "touchcancel",
        bridge.handleTouchCancel,
        TOUCH_LISTENER_OPTIONS,
      );
      window.removeEventListener("blur", bridge.cancel);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      bridge.cancel();
    };
  }, [containerRef, enabled]);
};
